/**
 * 投胎转世 — 沉浸式角色创建
 */

import type { PlayerState } from '../engine/Player';
import { getRootDisplayName, getRootDescription } from '../data/spiritualRoots';
import type { SpiritualRoot } from '../data/spiritualRoots';
import { getPlayerOrigin, getPlayerIdentity } from '../engine/Player';

export class ReincarnationPanel {
  private overlay: HTMLElement | null = null;
  private onComplete: ((name: string) => void) | null = null;

  start(player: PlayerState, callback: (name: string) => void): void {
    this.onComplete = callback;
    this.overlay = document.createElement('div');
    this.overlay.className = 'reincarnation-bg';
    this.overlay.innerHTML = `
      <div class="reincarnation-container">
        <div id="reincarnation-stage"></div>
      </div>
    `;
    document.body.appendChild(this.overlay);

    this.step1Root(player);
  }

  private async step1Root(player: PlayerState): Promise<void> {
    const stage = document.getElementById('reincarnation-stage')!;
    const root = player.spiritualRoot;
    const rootName = getRootDisplayName(root);
    const rootDesc = getRootDescription(root);
    const gradeEmoji: Record<string, string> = { '天': '👑', '地': '💎', '玄': '🔮', '黄': '🍂' };
    const emoji = gradeEmoji[root.grade] ?? '✨';

    stage.innerHTML = `
      <div class="reincarnation-step">
        <div class="reincarnation-particle"></div>
        <div style="font-size:1.2em;color:var(--text-secondary);margin-bottom:8px;">轮回转世 · 灵根觉醒</div>
        <div class="reincarnation-spirit-bg">
          <div class="reincarnation-spirit-circle">
            <div style="font-size:4em;">${emoji}</div>
          </div>
        </div>
        <div class="reincarnation-name" style="font-size:1.8em;color:var(--text-gold);margin-top:20px;">${rootName}</div>
        <div style="color:var(--text-secondary);margin-top:8px;max-width:360px;text-align:center;">${rootDesc}</div>
        <div style="margin-top:30px;color:var(--text-secondary);font-size:0.85em;">点击任意位置继续...</div>
      </div>
    `;

    await this.waitClick();
    this.step2Origin(player);
  }

  private async step2Origin(player: PlayerState): Promise<void> {
    const stage = document.getElementById('reincarnation-stage')!;
    const origin = getPlayerOrigin(player);
    const originIcon: Record<string, string> = {
      'cultivation_family': '🏛️', 'wealthy_mortal': '🏘️', 'farming_family': '🌾',
      'wandering_orphan': '🚶', 'war_refugee': '⛓️',
    };

    stage.innerHTML = `
      <div class="reincarnation-step">
        <div style="font-size:1.2em;color:var(--text-secondary);margin-bottom:8px;">红尘万丈 · 凡间出身</div>
        <div style="font-size:4em;">${originIcon[origin.id] ?? '🏠'}</div>
        <div class="reincarnation-name" style="font-size:1.5em;color:var(--accent);margin-top:12px;">${origin.name}</div>
        <div style="color:var(--text-secondary);margin-top:8px;max-width:360px;text-align:center;">${origin.desc}</div>
        <div style="margin-top:30px;color:var(--text-secondary);font-size:0.85em;">点击任意位置继续...</div>
      </div>
    `;

    await this.waitClick();
    this.step3Identity(player);
  }

  private async step3Identity(player: PlayerState): Promise<void> {
    const stage = document.getElementById('reincarnation-stage')!;
    const identity = getPlayerIdentity(player);
    const isRare = identity.id === 'master_disciple' || identity.id === 'imprisoned';
    const drama = identity.id === 'imprisoned' ? '命运多舛' : identity.id === 'master_disciple' ? '天赐机缘' : '';

    stage.innerHTML = `
      <div class="reincarnation-step">
        <div style="font-size:1.2em;color:var(--text-secondary);margin-bottom:8px;">命运之轮 · 仙途起点</div>
        ${isRare ? `<div class="reincarnation-drama">${drama}</div>` : ''}
        <div style="font-size:3em;">${identity.id === 'imprisoned' ? '⛓️' : identity.id === 'master_disciple' ? '👴' : identity.id === 'expelled' ? '💔' : identity.id.startsWith('sect') ? '🏯' : '🏚️'}</div>
        <div class="reincarnation-name" style="font-size:1.6em;color:var(--text-gold);margin-top:12px;">${identity.name}</div>
        <div style="color:var(--text-secondary);margin-top:8px;max-width:380px;text-align:center;">${identity.desc}</div>
        ${identity.id === 'imprisoned' ? '<div style="color:var(--danger);margin-top:12px;font-size:0.9em;">⛓️ 但天无绝人之路——逃出去，你将是涅槃重生的凤凰。</div>' : ''}
        ${identity.id === 'expelled' ? '<div style="color:var(--text-gold);margin-top:12px;font-size:0.9em;">💪 "资质不代表一切，韩立能做到的，我也能。"</div>' : ''}
        <div style="margin-top:30px;color:var(--text-secondary);font-size:0.85em;">点击任意位置继续...</div>
      </div>
    `;

    await this.waitClick();
    this.step4Name(player);
  }

  private step4Name(player: PlayerState): void {
    const stage = document.getElementById('reincarnation-stage')!;
    stage.innerHTML = `
      <div class="reincarnation-step">
        <div style="font-size:1.2em;color:var(--text-secondary);margin-bottom:8px;">名动四方 · 道号</div>
        <div style="font-size:3em;">📜</div>
        <div style="color:var(--text-secondary);margin-top:12px;">前世种种已随风而逝，留下你的名号：</div>
        <div style="margin-top:16px;">
          <input type="text" id="reincarnation-name-input" class="input-text"
            value="无名修士" maxlength="8" style="width:200px;text-align:center;font-size:1.2em;">
        </div>
        <div style="margin-top:20px;">
          <button class="btn btn-primary" id="reincarnation-confirm" style="font-size:1.1em;padding:10px 40px;">
            🌟 踏入仙途
          </button>
        </div>
      </div>
    `;

    const input = document.getElementById('reincarnation-name-input') as HTMLInputElement;
    const btn = document.getElementById('reincarnation-confirm')!;

    const confirm = () => {
      const name = input.value.trim() || '无名修士';
      if (this.overlay) {
        this.overlay.style.opacity = '0';
        this.overlay.style.transition = 'opacity 1s';
        setTimeout(() => {
          if (this.overlay) this.overlay.remove();
          this.overlay = null;
          if (this.onComplete) this.onComplete(name);
        }, 1000);
      }
    };

    btn.addEventListener('click', confirm);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') confirm(); });
    input.focus();
    input.select();
  }

  private waitClick(): Promise<void> {
    return new Promise((resolve) => {
      const handler = (e: Event) => {
        if ((e.target as HTMLElement)?.closest?.('button')) return;
        document.removeEventListener('click', handler);
        document.removeEventListener('keydown', keyHandler);
        resolve();
      };
      const keyHandler = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          document.removeEventListener('click', handler);
          document.removeEventListener('keydown', keyHandler);
          resolve();
        }
      };
      document.addEventListener('click', handler);
      document.addEventListener('keydown', keyHandler);
    });
  }
}
